/**
 * Recommended variant — shared keyframes + pure geometry helpers.
 *
 * Unlike the scoped-dark variants (TERM/BCAST/HUD/BOARD) this variant has NO
 * palette of its own: every colour comes from `useThemeColors()` (so it adapts
 * to light AND dark) plus the shared status metas in liveModeMeta. This file only
 * holds presentation constants that don't depend on the theme.
 */
import { typography } from '@/shared/theme/tokens';

export const MONO = typography.fontFamily.mono;

// Presentation-only keyframes. Every use is gated on the model's `animate` flag
// (false under reduced-motion). Motion only ever means "this just changed".
export const KEYFRAMES = `
@keyframes rec-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
@keyframes rec-flip { 0% { transform: translateY(-42%) rotateX(-86deg); opacity: 0.2; } 100% { transform: none; opacity: 1; } }
@keyframes rec-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@keyframes rec-flash { 0% { opacity: 0.2; } 100% { opacity: 1; } }
@keyframes rec-in { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
`;

// ── Arc-gauge geometry (270° dial, gap at 6 o'clock) — ported from the HUD
// variant. Pure functions, theme-independent. ───────────────────────────────
export const GAUGE_START = 225;
export const GAUGE_SWEEP = 270;

function pointOnArc(cx, cy, r, clockDeg) {
  const a = ((clockDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}
const round = (n) => Math.round(n * 100) / 100;

export function arcPath(cx, cy, r, startDeg, endDeg) {
  const s = pointOnArc(cx, cy, r, startDeg);
  const e = pointOnArc(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${round(s.x)} ${round(s.y)} A ${r} ${r} 0 ${large} 1 ${round(e.x)} ${round(e.y)}`;
}

export function dialPoint(cx, cy, r, fraction) {
  const f = Math.min(1, Math.max(0, fraction));
  return pointOnArc(cx, cy, r, GAUGE_START + f * GAUGE_SWEEP);
}

export const clamp01 = (x) => Math.min(1, Math.max(0, x));
